<template>
  <div>
    <h1 class="card-container-title mb-2 d-flex align-center">Redirecionamento</h1>
    <mf-tabs-button v-model="tab" class="mt-3" :buttons="tabs" />

    <div v-if="tab === 'external'">
      <p class="hint-text external mt-5">
        Nessa modalidade o usuário é redirecionado para uma página web externa. Só poderemos levar seus consumidores para endereços seguros, por isso o único
        tipo de link permitido é o que começa com <b>https://</b>. Certifique-se de inserir um link seguro aqui:
      </p>
      <v-text-field
        v-model="external"
        dense
        placeholder="https://"
        label="Link"
        outlined
        :error="externalError"
        :error-messages="externalError ? 'Você precisa incluir um link válido.' : ''"
      />
    </div>

    <div v-if="tab === 'internal'">
      <p class="hint-text mt-5 mb-0">Selecione a tela do aplicativo para onde o push deve redirecionar.</p>
      <v-radio-group v-model="internal" dense hide-details class="mt-0">
        <v-row>
          <v-col v-for="(item, index) in links" :key="index" cols="3">
            <mf-tooltip top max-width="250px" variant="secondary" :disabled="hideTooltip(item)">
              <template #activator="{ on }">
                <v-radio v-if="item" :label="item.title" color="primary" :value="item.link" v-on="on" />
              </template>
              <span>O cliente será levado à página inicial do E-commerce</span>
            </mf-tooltip>
          </v-col>
        </v-row>
      </v-radio-group>

      <div v-if="internal === 'FlyersScreen'">
        <p class="hint-text mt-2 mb-2">Caso queira redirecionar o consumidor para um encarte específico, selecione abaixo:</p>
        <v-select v-model="selectedFlyer" item-value="id" outlined :items="flyersOptions" label="Encarte" item-text="title" style="width: 300px" dense />
      </div>
    </div>

    <div v-if="tab === 'offer'" class="mt-5">
      <div class="offer-message-container">
        <p><mf-icon>add_to_home_screen</mf-icon></p>
        <p class="my-1">
          O push cadastrado redirecionará os consumidores para as ofertas selecionadas anteriormente de acordo com o produto que estiver sendo divulgado.
          Ofertas selecionadas para essa campanha:
        </p>
        <p v-for="(offer, index) in offers" :key="`${offer}-${index}`" class="offer-title mb-1">{{ offer }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'

import { QUERY_GET_APP_LINKS } from '@/modules/mobile/banners/graphql'
import { QUERY_FLYERS } from '@/modules/mobile/encartes/graphql'
import { channelsForm } from '@/modules/maria/campanha-da-maria-v2/mixins/channels-form'
import { notOnlySpace, isUrl } from '@/helpers/validators.js'

const TAB_TYPES = {
  EXTERNAL: 'external',
  EXTERNAL_LINK: 'external',
  INTERNAL: 'internal',
  OFFER: 'offer',
}

const ACTION_TYPES = {
  EXTERNAL: 'EXTERNAL',
  EXTERNAL_LINK: 'EXTERNAL_LINK',
  INTERNAL: 'INTERNAL',
  INTERNAL_PAGE: 'INTERNAL_PAGE',
}

export default {
  name: 'Redirection',
  mixins: [channelsForm],

  data: () => ({
    flyersOptions: [],
    tab: TAB_TYPES.OFFER,
    external: '',
    internal: 'home',
    selectedFlyer: '',
  }),
  computed: {
    channelSettings() {
      return this.campaignSettings?.channels?.push || {}
    },
    externalError() {
      return this.$v?.external?.$error
    },
    getAction() {
      const actions = {
        [TAB_TYPES.EXTERNAL]: {
          type: ACTION_TYPES.EXTERNAL,
          link: this.external,
          reference_id: null,
        },
        [TAB_TYPES.INTERNAL]: {
          type: ACTION_TYPES.INTERNAL,
          link: this.internal === 'home' ? '' : this.internal,
          reference_id: this.internal === 'FlyersScreen' ? this.selectedFlyer : null,
        },
        [TAB_TYPES.OFFER]: {
          type: ACTION_TYPES.INTERNAL,
          link: 'SalesOmniCampaignScreen',
          reference_id: null,
        },
      }

      return actions[this.tab] || {}
    },
    offers() {
      return Array.isArray(this.campaign.offer_schedules) ? this.campaign.offer_schedules.map((offer) => offer.name) : []
    },
    tabs() {
      return [
        {
          id: 0,
          label: 'Link externo',
          value: TAB_TYPES.EXTERNAL,
          active: true,
        },
        {
          id: 1,
          label: 'Link interno',
          value: TAB_TYPES.INTERNAL,
          active: true,
        },
        {
          id: 2,
          label: 'Vincular oferta',
          value: TAB_TYPES.OFFER,
          active: true,
        },
      ]
    },
    links() {
      const home = {
        title: 'Página inicial',
        link: 'home',
      }

      return [home].concat(this.appPagesLinks)
    },
  },
  apollo: {
    appPagesLinks: {
      query: QUERY_GET_APP_LINKS,
    },
    flyers: {
      query: QUERY_FLYERS,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables() {
        return {
          status: ['ACTIVE', 'SCHEDULED'],
        }
      },
      update(data) {
        this.flyersOptions = data.flyers.flyers.map((flyer) => ({
          title: flyer.title,
          id: flyer._id,
          initialDate: flyer.initial_date,
          finalDate: flyer.final_date,
        }))
      },
    },
  },
  mounted() {
    this.initializeTab()
  },
  methods: {
    hideTooltip(item) {
      const isEcommerce = this.$getLoggedUser().has_ecommerce
      if (!isEcommerce) return true
      return item && item.link !== 'InternalEcommerce'
    },
    initializeTab() {
      if (this.channelSettings?.message?.action) {
        const { action } = this.channelSettings.message
        if (action?.type) {
          this.handleActionType(action)
        }
      } else {
        this.tab = TAB_TYPES.OFFER
      }

      if (!this.offers.length) {
        this.tab = TAB_TYPES.EXTERNAL
      }
    },
    handleActionType(action) {
      const { link, type, reference_id } = action

      if (type === ACTION_TYPES.INTERNAL_PAGE || type === ACTION_TYPES.INTERNAL) {
        if (link === 'SalesOmniCampaignScreen') {
          this.$nextTick(() => {
            this.tab = TAB_TYPES.OFFER
          })
        } else {
          this.$nextTick(() => {
            this.tab = TAB_TYPES.INTERNAL
            this.internal = link || 'home'
            this.selectedFlyer = link === 'FlyersScreen' ? reference_id : null
          })
        }
      } else if (type === ACTION_TYPES.EXTERNAL || type === ACTION_TYPES.EXTERNAL_LINK) {
        this.$nextTick(() => {
          this.tab = TAB_TYPES.EXTERNAL
          this.external = link
        })
      } else {
        this.$nextTick(() => {
          this.tab = TAB_TYPES.OFFER
        })
      }
    },
    validInput() {
      if (this.tab !== TAB_TYPES.EXTERNAL) return true

      this.$v.$reset()
      this.$v.$touch()
      return !this.$v.$error
    },
  },
  validations() {
    return {
      external: {
        required,
        notOnlySpace,
        isUrl,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.card-container-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 22px;
  flex: 1;
  color: $cinza-d10;
  margin: 0;
  gap: 12px;
}

.hint-text {
  font-size: 14px;
  line-height: 20px;
  max-width: 700px;

  b {
    color: $marca-1;
    font-weight: 700;
  }
}

.offer-title {
  color: $marca-0-l40 !important;
  font-family: Lato;
  font-weight: 700 !important;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0%;
}

.offer-message-container {
  padding: 24px;
  background: $cinza-l90;
  h2 {
    font-weight: 700;
    color: $cinza-d20;
    font-size: 18px;
    text-align: center;
  }

  p {
    font-weight: 400;
    color: #656565;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    margin: 0 auto;
    max-width: 730px;
  }
}

.sticky-headers {
  background: $branco !important;
}
</style>
